import { RcsParser, getChildrenFromMask } from './RcsParser.js';
import { MIN_LEVELS_IN_LOD0, RCS_HEADER_BYTES, RCS_BYTES_PER_SVO_POINT } from './RcsConstants.js';
import { createRcsIO } from './RcsIO.js';
import { calculateLevelsToProcessForLod } from './RcsLodMapping.js';

/**
 * Some information about the RCS file format is available on this wiki page:
 * https://autodesk.atlassian.net/wiki/spaces/aeceng/pages/350480523/ReCap+File+Format
 */

/**
 * Parsed RCS file header.
 * @typedef {Object} RcsHeader
 * @property {string}    magic - File magic string ("RCS")
 * @property {number}    majorVersion
 * @property {number}    minorVersion
 * @property {number[]}  translation - [x, y, z]
 * @property {number[]}  rotation - [x, y, z]
 * @property {number[]}  scale - [x, y, z]
 * @property {number[]}  scannerOrigin - [x, y, z]
 * @property {number[]}  svoBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}  scanBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number}    octreeFlags
 * @property {number}    provider
 * @property {number}    totalPoints
 * @property {boolean}   hasRGB
 * @property {boolean}   hasNormals
 * @property {boolean}   hasIntensity
 * @property {string}    scanId
 * @property {string}    coordinateSystem
 * @property {number}    numFileChunks
 * @property {number}    chunkDirectoryOffset
 */

/**
 * RCS file chunk directory entry.
 * @typedef {Object} RcsChunk
 * @property {number} chunkId - Chunk type ID (1 = voxel data, 2 = umbrella octree, 3 = umbrella extended)
 * @property {number} fileOffsetStart - Byte offset in file
 * @property {number} sizeInBytes
 * @property {string} name - Human-readable chunk name
 */

/**
 * Umbrella octree leaf node — one per scan region.
 * @typedef {Object} UmbrellaLeafNode
 * @property {number}   index - Leaf index within the umbrella array
 * @property {number[]} svoBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]} nodeBounds - [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number}   maxDepth - Maximum octree depth for this leaf
 * @property {number}   amountOfPoints - Total point count
 * @property {number}   fileSize - Size of the SVO payload in the file
 * @property {number[]} numNonLeafNodes - Interior node counts per level (length 32)
 * @property {number[]} numSvoLeafNodes - SVO leaf point counts per level (length 32)
 */

/**
 * Core RCS Extractor that works in both Node.js and browsers
 * Coordinates I/O operations with parsing logic
 */
export class RcsExtractorCore {
  /**
   * @param {string|null} source - URL of the RCS file or null for the VS production proxy where the resource is identified by URN rather than URL
   * @param {(() => (string|null))} [getAuthUrn] - Getter returning the manifest URN for WebSocket proxy auth
   * @param {() => (string|null)} [getToken] - Getter returning the current access token. Called per request so token refreshes are picked up; the returned value must not be cached.
   * @param {() => (string|null)} [getAcmSessionId] - Getter returning the ACM session ID for proxy auth
   */
  constructor(source, getAuthUrn, getToken, getAcmSessionId) {
    this.source = source;

    this.io = createRcsIO(source, getAuthUrn, getToken, getAcmSessionId);

    /** @type {RcsHeader|null} */
    this.header = null;
    /** @type {RcsChunk[]} */
    this.chunks = [];
    /** @type {UmbrellaLeafNode[]|null} */
    this.umbrellaOctreeData = null;
    /** @type {{ intermediateNodes: any[], nodeLevelStartIndices: number[], levelNodeCounts: number[] }|null} */
    this.umbrellaExtendedData = null;
    this.loadedUmbrellaExtendedData = null;
  }

  async open() {
    await this.io.open();
  }

  async close() {
    await this.io.close();
  }

  /**
   * Read the raw RCS file header bytes (includes the chunk directory).
   * @returns {Promise<Uint8Array>}
   */
  async readHeaderBytes() {
    return this.io.readRange(0, RCS_HEADER_BYTES);
  }

  /**
   * Parse a raw header buffer and populate {@link header} and {@link chunks}.
   * @param {Uint8Array} headerData
   * @returns {{ header: RcsHeader, chunks: RcsChunk[] }}
   */
  parseHeaderBytes(headerData) {
    this.header = RcsParser.parseHeader(headerData);
    this.chunks = RcsParser.parseChunkDirectory(
      headerData,
      this.header.chunkDirectoryOffset,
      this.header.numFileChunks
    );
    return { header: this.header, chunks: this.chunks };
  }

  /**
   * Read and parse the header and chunk directory
   * @returns {Promise<{ header: RcsHeader, chunks: RcsChunk[] }>}
   */
  async readHeaderAndChunkDirectory() {
    const headerData = await this.readHeaderBytes();
    return this.parseHeaderBytes(headerData);
  }

  /**
   * Read the raw umbrella octree chunk (chunk 2) bytes.
   * Requires {@link chunks} to be populated first.
   * @returns {Promise<Uint8Array>}
   */
  async readOctreeChunkBytes() {
    const chunk = this.chunks.find((c) => c.chunkId === 2);
    if (!chunk) {
      throw new Error('Umbrella octree data chunk (ID=2) not found');
    }
    return this.io.readRange(Number(chunk.fileOffsetStart), Number(chunk.sizeInBytes));
  }

  /**
   * Read umbrella octree data (Chunk 2) - cached for efficiency
   * @returns {Promise<UmbrellaLeafNode[]>}
   */
  async readUmbrellaOctreeData() {
    if (this.umbrellaOctreeData) {
      return this.umbrellaOctreeData;
    }

    const chunkData = await this.readOctreeChunkBytes();
    this.umbrellaOctreeData = RcsParser.parseUmbrellaOctreeData(chunkData);
    return this.umbrellaOctreeData;
  }

  /**
   * Compute umbrella extended data from the header SVO bounds and chunk 2 leaf
   * summaries. Cached for efficiency.
   *
   * @param {number[]} svoBounds - Umbrella root cube from the file header
   * @param {UmbrellaLeafNode[]} leafNodes - Parsed umbrella leaf nodes (chunk 2)
   * @returns {Promise<UmbrellaExtendedData>}
   */
  async computeUmbrellaExtendedData(svoBounds, leafNodes) {
    if (this.umbrellaExtendedData) {
      return this.umbrellaExtendedData;
    }

    this.umbrellaExtendedData = RcsParser.computeUmbrellaExtendedData(
      svoBounds, leafNodes
    );
    return this.umbrellaExtendedData;
  }

  /**
   * Read umbrella extended data (chunk 3) from the file. Used for diagnostics and
   * parity tests; the production load path uses {@link computeUmbrellaExtendedData}.
   */
  async readUmbrellaExtendedData() {
    if (this.loadedUmbrellaExtendedData) {
      return this.loadedUmbrellaExtendedData;
    }

    const chunk = this.chunks.find((c) => c.chunkId === 3);
    if (!chunk) {
      throw new Error('Umbrella extended data chunk (ID=3) not found');
    }

    const chunkData = await this.io.readRange(Number(chunk.fileOffsetStart), Number(chunk.sizeInBytes));
    this.loadedUmbrellaExtendedData = RcsParser.parseUmbrellaExtendedData(chunkData);
    return this.loadedUmbrellaExtendedData;
  }

  /**
   * Calculate the required data offset and size for SVO payload based on LOD range
   * Uses the same algorithm as the decoder to determine exact byte requirements
   * @param {UmbrellaLeafNode} leafNode - The leaf node containing level information
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {number} [levelsInLod0] - Per-model LOD-0 source level count ('direct' mode only).
   * @returns {{ offset: number, size: number }}
   */
  _calculateRequiredDataSize(leafNode, lodLevel, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
    // Size for interior nodes (if we need any)
    const numInteriorNodes = leafNode.numNonLeafNodes.reduce((sum, count) => sum + count, 0);
    const numSvoLeafNodeTotal = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    const interiorNodeDataSize = (numInteriorNodes + numSvoLeafNodeTotal) * 4;

    const levelInfo = this._calculateLevelsToProcessForSize(leafNode, lodLevel, levelsInLod0);

    // If no levels to process, return early with size 0
    if (levelInfo.levelsToProcess.length === 0) {
      return {
        offset: interiorNodeDataSize,
        size: 0 // Read nothing
      };
    }

    // Calculate offset for the leaf nodes we need
    const { leafStart, leafEnd } = levelInfo.nodeRanges;

    // Check if we're trying to start beyond available data
    // This can happen when the leaf node doesn't have enough points for this LOD level
    const totalLeafNodes = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);
    if (leafStart >= totalLeafNodes) {
      // No data to load - return safe values (start after interior nodes with 0 size)
      return {
        offset: interiorNodeDataSize,
        size: 0 // Read nothing
      };
    }

    // Clamp leafEnd to totalLeafNodes to avoid reading beyond available data
    const clampedLeafEnd = Math.min(leafEnd, totalLeafNodes);

    // Fetch only the leaf points for the requested level(s): [leafStart, leafEnd).
    // The buffer therefore begins exactly at leafStart, so decodeSvoPointsDirect
    // (interiorNodesIncluded=false) reads from offset 0 without skipping a prefix.
    const dataOffset = interiorNodeDataSize + leafStart * RCS_BYTES_PER_SVO_POINT;
    const dataSize = (clampedLeafEnd - leafStart) * RCS_BYTES_PER_SVO_POINT;

    const maxSize = leafNode.fileSize - dataOffset;
    return {
      offset: dataOffset,
      size: Math.max(0, Math.min(dataSize, maxSize))
    };
  }

  /**
   * Calculate levels to process for size estimation based on point or level range
   * @param {UmbrellaLeafNode} leafNode
   * @param {number} lodLevel - Target LOD level index
   * @param {number} [levelsInLod0] - Per-model LOD-0 source level count ('direct' mode only).
   * @private
   */
  _calculateLevelsToProcessForSize(leafNode, lodLevel, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;

    const { levelsToProcess, pointsBeforeStart, totalPoints } =
    calculateLevelsToProcessForLod(numSvoLeafNodes, lodLevel, levelsInLod0);

    // Calculate node ranges for size estimation
    const nodeRanges = this._calculateNodeRangesForSize(numSvoLeafNodes, levelsToProcess);

    return {
      levelsToProcess,
      nodeRanges,
      actualStartPoint: pointsBeforeStart,
      actualEndPoint: pointsBeforeStart + totalPoints
    };
  }

  /**
   * Calculate node ranges for size estimation (simplified version of parser logic)
   * @param {number[]} numSvoLeafNodes
   * @param {number[]} levelsToProcess
   * @private
   */
  _calculateNodeRangesForSize(numSvoLeafNodes, levelsToProcess) {
    if (levelsToProcess.length === 0) {
      return { leafStart: 0, leafEnd: 0 };
    }

    const minLevel = Math.min(...levelsToProcess);
    const maxLevel = Math.max(...levelsToProcess);

    // Calculate leaf node ranges
    let leafStart = 0;
    let leafEnd = 0;

    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) {
        leafStart += numSvoLeafNodes[level] || 0;
      }
      if (level <= maxLevel) {
        leafEnd += numSvoLeafNodes[level] || 0;
      }
    }

    return { leafStart, leafEnd };
  }
}

// Export functions for standalone use
export { getChildrenFromMask };